import { ApplicationQuestionAndAnswerDto, CustomQuestionResponseTypeEnum } from '@inosio/payments-sdk';
import { applicationStyles } from './CustomStyles';
import { Box, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import { InputField } from '../../../components/InputController';
import { Fragment } from 'react';

interface CustomQuestionsSectionProps {
  /**
   * Array of all questions and any answers if they have them.
   */
  questionsAndAnswers: ApplicationQuestionAndAnswerDto[];

  /**
   * Query param on whether to mark the fields as failed validation to bring attention to the user.
   */
  hasValidationQueryParam: boolean;

  /**
   * Event to fire every time the value of the field changes. Useful for setting the form state.
   */
  onFieldChange: ((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLInputElement>, questionUuid: string) => void) | undefined;

  /**
   * Event to track when we believe the user is finished modifying this field
   */
  onFieldUpdate: ((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLInputElement>, questionUuid: string) => void) | undefined;

  /**
   *
   */
  bottomRef?: React.RefObject<HTMLElement>; //WTF is this?

  hideHeader?: boolean;
}

export default function CustomQuestionsSection(props: CustomQuestionsSectionProps) {
  const classes = applicationStyles();
  return (
    <>
      {!!props.questionsAndAnswers?.length && (
        <Grid item xs={12}>
          {!props.hideHeader && (
            <Typography ref={props.bottomRef} sx={{ mb: 2 }} className={classes.labelText}>
              Additional Questions
            </Typography>
          )}
          {props.questionsAndAnswers
            .filter((q) => !q.ParentCustomQuestionUuid)
            .map((question) => {
              let returnElement;

              if (question.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
                returnElement = (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ mr: 3 }} className={classes.labelText}>
                      {question.QuestionText}
                      {question.IsRequired && <span style={{ color: 'red' }}>*</span>}
                    </Typography>
                    <RadioGroup
                      value={question.BooleanQuestionAnswer ?? null}
                      row
                      onChange={(e: any) => {
                        //* due to some issues with certain browsers not supporting onBlur correctly, we fire the save event here for radios.
                        if (props.onFieldChange) props.onFieldChange(e, question.Uuid!);
                        if (props.onFieldUpdate) props.onFieldUpdate(e, question.Uuid!);
                      }}
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio size="small" />}
                        sx={question.BooleanQuestionAnswer == null && question.IsRequired && props.hasValidationQueryParam ? { color: 'red' } : {}}
                        label="Yes"
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio size="small" />}
                        sx={question.BooleanQuestionAnswer == null && question.IsRequired && props.hasValidationQueryParam ? { color: 'red' } : {}}
                        label="No"
                      />
                    </RadioGroup>
                  </Box>
                );
              } else if (question.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.FreeText) {
                returnElement = (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ mr: 3, mb: 2 }} className={classes.labelText}>
                      {question.QuestionText}
                      {question.IsRequired && <span style={{ color: 'red' }}>*</span>}
                    </Typography>
                    <Box sx={{ width: 360 }}>
                      <InputField
                        name={question.QuestionText}
                        value={question.TextQuestionAnswer}
                        errors={question.IsRequired && !question.TextQuestionAnswer ? 'Required Field' : ''}
                        isError={question.IsRequired && !question.TextQuestionAnswer && props.hasValidationQueryParam}
                        onChange={(e) => {
                          if (props.onFieldChange) props.onFieldChange(e, question.Uuid!);
                        }}
                        onBlur={(e) => {
                          if (props.onFieldUpdate) props.onFieldUpdate(e, question.Uuid!);
                        }}
                      />
                    </Box>
                  </Box>
                );
              }

              const chidrenElement = props.questionsAndAnswers
                .filter((cq) => cq.ParentCustomQuestionUuid === question.Uuid)
                .map((childQuestion) => {
                  if (childQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }} key={childQuestion.Uuid}>
                        <Typography sx={{ mr: 3 }} className={classes.labelText}>
                          {childQuestion.QuestionText}
                          {childQuestion.IsRequired && <span style={{ color: 'red' }}>*</span>}
                        </Typography>
                        <RadioGroup
                          value={childQuestion.BooleanQuestionAnswer ?? null}
                          row
                          onChange={(e) => {
                            if (props.onFieldChange) props.onFieldChange(e, childQuestion.Uuid!);
                            if (props.onFieldUpdate) props.onFieldUpdate(e, childQuestion.Uuid!);
                          }}
                        >
                          <FormControlLabel
                            value={true}
                            control={<Radio size="small" />}
                            sx={childQuestion.BooleanQuestionAnswer == null && childQuestion.IsRequired && props.hasValidationQueryParam ? { color: 'red' } : {}}
                            label="Yes"
                          />
                          <FormControlLabel
                            value={false}
                            control={<Radio size="small" />}
                            sx={childQuestion.BooleanQuestionAnswer == null && childQuestion.IsRequired && props.hasValidationQueryParam ? { color: 'red' } : {}}
                            label="No"
                          />
                        </RadioGroup>
                      </Box>
                    );
                  } else if (childQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.FreeText) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center' }} key={childQuestion.Uuid}>
                        <Typography sx={{ mr: 3, mb: 2 }} className={classes.labelText}>
                          {childQuestion.QuestionText}
                          {childQuestion.IsRequired && <span style={{ color: 'red' }}>*</span>}
                        </Typography>
                        <Box sx={{ width: 360 }}>
                          <InputField
                            name={childQuestion.QuestionText}
                            value={childQuestion.TextQuestionAnswer}
                            isError={childQuestion.IsRequired && !childQuestion.TextQuestionAnswer && props.hasValidationQueryParam}
                            errors={childQuestion.IsRequired && !childQuestion.TextQuestionAnswer ? 'Required Field' : ''}
                            onChange={(e) => {
                              if (props.onFieldChange) props.onFieldChange(e, childQuestion.Uuid!);
                            }}
                            onBlur={(e) => {
                              if (props.onFieldUpdate) props.onFieldUpdate(e, childQuestion.Uuid!);
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  } else {
                    return <></>;
                  }
                });

              return (
                <Fragment key={question.Uuid}>
                  {returnElement}
                  {(question.TextQuestionAnswer || question.BooleanQuestionAnswer) && <Box sx={{ ml: 5 }}>{chidrenElement}</Box>}
                </Fragment>
              );
            })}
        </Grid>
      )}
    </>
  );
}
